import { Button, HStack, Text, VStack } from '@chakra-ui/react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import Image from 'components/Image'
import { useStores } from 'hooks/useStores'
import { INotificationWithRelations } from 'interfaces/notification'
import styles from '../../styles.module.scss'

/** Slim metadata blob written at notification creation time (invitation.service.ts). */
interface InvitationNotifMetadata {
  v?: number
  organizationName?: string
  inviterName?: string
  invitedUserName?: string
}

function parseMetadata(raw?: string): InvitationNotifMetadata {
  if (!raw) return {}
  try {
    return JSON.parse(raw) as InvitationNotifMetadata
  } catch {
    return {}
  }
}

const OrgInvitationNotification = ({ notification }: { notification: INotificationWithRelations }) => {
  const { notificationStore } = useStores()

  // INotification.id is typed optional (id?: number) but DB rows always carry one.
  // Guard against the type hole: a shared 0-fallback would collide busy state across
  // any id-less notifications that slipped through.
  const notifId = notification.id
  if (notifId == null) return null

  const invitation = notification.invitation
  const status = invitation?.status ?? 'expired'
  const isPending = status === 'pending'
  const meta = parseMetadata(notification.metadata)

  // Read busy state from store (prevents double-click and keeps UI consistent
  // if the same notification appears in multiple places simultaneously).
  const busy = notificationStore.getMutatingState(notifId)

  const inviterName =
    meta.inviterName ||
    [notification.author?.firstName, notification.author?.lastName].filter(Boolean).join(' ').trim() ||
    'Someone'
  // Fallback chain for team name:
  // 1. metadata.organizationName (set at create time, fastest path)
  // 2. notification.invitation.organization.name (relation; covers stale or
  //    legacy notifications missing v=2 metadata)
  // 3. 'the team' (last-resort string — preferred over the older "an
  //    organization" wording so the sentence still reads naturally)
  const orgName =
    meta.organizationName ||
    invitation?.organization?.name ||
    'the team'
  // Guardian-relayed invite: `invitedUserName` is set when `targetUserId` is a child
  // managed by the guardian. UI swaps "you" for the child's name so the guardian
  // sees clearly who the invite is for.
  const invitedSubject = meta.invitedUserName?.trim() || 'you'

  const handleAccept = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!invitation?.token || busy) return
    notificationStore.acceptInvitation(notifId, invitation.token)
  }

  const handleDeny = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!invitation?.token || busy) return
    notificationStore.denyInvitation(notifId, invitation.token)
  }

  return (
    <HStack alignItems="flex-start" w="100%">
      <Image containerClassName={styles.avatar} src={notification?.author?.image} alt="inviter avatar" />
      <VStack alignItems="flex-start" spacing={2} flex={1}>
        <Text className={styles.textContent} m={0}>
          <span className={cx(styles.object)}>{inviterName}</span> invited{' '}
          {invitedSubject === 'you' ? (
            'you'
          ) : (
            <span className={cx(styles.object)}>{invitedSubject}</span>
          )}{' '}
          to join <span className={cx(styles.object)}>{orgName}</span>
        </Text>

        {isPending && (
          <HStack spacing={2}>
            <Button
              size="xs"
              onClick={handleAccept}
              isLoading={busy === 'accept'}
              isDisabled={busy === 'deny'}
              bg="blue.500"
              color="white"
              border="none"
              _hover={{ bg: 'blue.600' }}
              _focus={{ boxShadow: 'none', outline: 'none' }}
              _focusVisible={{ boxShadow: 'none', outline: 'none' }}
              _active={{ boxShadow: 'none', bg: 'blue.600' }}
            >
              Accept
            </Button>
            <Button
              size="xs"
              onClick={handleDeny}
              isLoading={busy === 'deny'}
              isDisabled={busy === 'accept'}
              bg="whiteAlpha.200"
              color="white"
              border="none"
              _hover={{ bg: 'whiteAlpha.300' }}
              _focus={{ boxShadow: 'none', outline: 'none' }}
              _focusVisible={{ boxShadow: 'none', outline: 'none' }}
              _active={{ boxShadow: 'none', bg: 'whiteAlpha.300' }}
            >
              Deny
            </Button>
          </HStack>
        )}

        {!isPending && (
          <Text fontSize="xs" opacity={0.6} m={0}>
            {status === 'accepted' && 'Joined'}
            {status === 'declined' && 'Declined'}
            {status === 'expired' && 'Expired'}
            {status === 'revoked' && 'Revoked'}
          </Text>
        )}
      </VStack>
    </HStack>
  )
}

export default observer(OrgInvitationNotification)
